// 殻: 左ナビ（今日やること／利用者／設定）＋上部（事業所名と切替）＋本文。狭い画面ではナビをドロワーにする。
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Building2, ClipboardList, Menu, Settings, Shield, Users, X } from 'lucide-react';
import { isOperator } from '../lib/domain';
import { useOffice } from '../lib/office';
import { Button, Card, ErrorText, Loading, cx } from './ui';

function NavItem({ to, icon, label, testId, onNavigate }: { to: string; icon: ReactNode; label: string; testId?: string; onNavigate?: () => void }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onNavigate}
      className={({ isActive }) =>
        cx('flex h-11 items-center gap-2.5 rounded-md px-3 text-[15px]', isActive ? 'bg-accent-lt font-medium text-accent' : 'text-ink-2 hover:bg-subtle')
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

function Nav({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useOffice();
  return (
    <nav className="flex h-full flex-col gap-1 p-3" aria-label="メインメニュー">
      <div className="mb-3 px-3 text-lg font-semibold tracking-tight text-accent">Alfine</div>
      <NavItem to="/dashboard" icon={<ClipboardList size={18} />} label="今日やること" testId="nav-dashboard" onNavigate={onNavigate} />
      <NavItem to="/clients" icon={<Users size={18} />} label="利用者" testId="nav-clients" onNavigate={onNavigate} />
      <div className="flex-1" />
      {isOperator(me) ? <NavItem to="/ops" icon={<Shield size={18} />} label="運営" testId="nav-ops" onNavigate={onNavigate} /> : null}
      <NavItem to="/settings" icon={<Settings size={18} />} label="設定" testId="nav-settings" onNavigate={onNavigate} />
    </nav>
  );
}

/** 事業所の切替。所属が 1 つなら名前だけ出す */
export function OfficeSwitcher({ className, testId = 'office-switcher' }: { className?: string; testId?: string }) {
  const { me, officeId, office, selectOffice } = useOffice();
  const loc = useLocation();
  const navigate = useNavigate();
  const change = (id: string) => {
    selectOffice(id);
    // 利用者は事業所ごとなので、利用者の画面にいたら一覧へ戻す
    if (loc.pathname.startsWith('/clients/')) navigate('/clients');
  };
  const offices = me?.principal.offices ?? [];
  if (offices.length === 0) return null;
  if (offices.length === 1) {
    return (
      <span data-testid={testId} className={cx('flex items-center gap-1.5 truncate text-sm text-ink-2', className)}>
        <Building2 size={16} className="shrink-0 text-ink-3" />
        {office?.officeName ?? offices[0]!.officeName}
      </span>
    );
  }
  return (
    <label className={cx('flex items-center gap-1.5 text-sm text-ink-2', className)}>
      <Building2 size={16} className="shrink-0 text-ink-3" />
      <span className="sr-only">事業所</span>
      <select
        data-testid={testId}
        value={officeId ?? ''}
        onChange={(e) => e.target.value && change(e.target.value)}
        className="h-9 max-w-[16rem] truncate rounded-md border border-line bg-surface px-2 text-ink-1"
      >
        {officeId ? null : <option value="">事業所を選ぶ</option>}
        {offices.map((o) => (
          <option key={o.officeId} value={o.officeId}>
            {o.officeName}（{o.corpName}）
          </option>
        ))}
      </select>
    </label>
  );
}

export function Shell() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="flex min-h-dvh bg-bg">
      <aside className="sticky top-0 hidden h-dvh w-56 shrink-0 border-r border-line bg-sidebar lg:block" data-testid="side-nav">
        <Nav />
      </aside>

      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-line bg-sidebar shadow-xl">
            <button type="button" aria-label="メニューを閉じる" className="absolute right-2 top-2 rounded p-2 text-ink-3 hover:bg-subtle" onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
            <Nav onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-bg/95 px-4 backdrop-blur">
          <button type="button" aria-label="メニュー" data-testid="mobile-menu-toggle" className="-ml-2 rounded p-2 text-ink-2 hover:bg-subtle lg:hidden" onClick={() => setOpen(true)}>
            <Menu size={20} />
          </button>
          <OfficeSwitcher className="min-w-0" />
        </header>
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** 事業所の文脈が要る画面の前に置く。未選択なら選ばせる */
export function NeedOffice({ children }: { children: (officeId: string) => ReactNode }) {
  const { me, loading, error, officeId, reload } = useOffice();
  if (loading) return <Loading />;
  if (error)
    return (
      <div className="space-y-3">
        <ErrorText>{error}</ErrorText>
        <Button variant="secondary" onClick={reload}>
          読み込み直す
        </Button>
      </div>
    );
  if (!me || me.principal.offices.length === 0)
    return <Card className="text-ink-2">所属している事業所がありません。管理者に招待を依頼してください。</Card>;
  if (!officeId)
    return (
      <Card className="space-y-3">
        <p className="text-ink-2">使う事業所を選んでください。</p>
        <OfficeSwitcher testId="office-picker" />
      </Card>
    );
  return <>{children(officeId)}</>;
}
